import { isSome } from '@mbfab/shared'
import { Fragment, useState } from 'react'
import type { ReactNode } from 'react'

import { TableColumnMenu } from './column-menu'
import { DataTableShell } from './data-table-shell'
import { DataTableEmptyRow } from './empty-row'
import { ExpandToggleButton } from './expand-toggle-button'
import { OverflowText } from './overflow-text'
import type { OverflowMode } from './overflow-text'
import { SortIndicator } from './sort-indicator'
import { nextSorters } from './sorters'
import type { DataTableSorter } from './sorters'
import {
  dataTableHeadRowClass,
  dataTableRowClass,
  dataTableTdClass,
  dataTableThClass,
  keyColumnShadowClass,
  stickyCellBgClass,
} from './styles'

import { CheckboxControl } from '@/components/ui/checkbox-control'
import { SelectionActionBar } from '@/components/ui/selection-action-bar'
import { hasPagesToShow, TablePagination } from '@/components/ui/table-pagination'
import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/data-table.md

// DataTableSorter は sorters.ts が正（nextSorters/applySorters と同じ型を共有するため）。
// DataTable の公開 API としてはこのファイルから再 export する
export type { DataTableSorter } from './sorters'
export { applySorters, nextSorters } from './sorters'

export type DataTableColumn<Row> = {
  key: string
  title: ReactNode
  render: (row: Row) => ReactNode
  sortable?: boolean
  numeric?: boolean
  fixed?: 'left' | 'right'
  minWidthClass?: string
  help?: ReactNode
  hidable?: boolean
  // render が文字列を返したときの長文の見せ方（既定 'truncate'＝最大幅で1行省略＋全文ポップオーバー）。
  // ReactNode を返す列は対象外なので、装飾付きの長文は呼び出し側で OverflowText を使う
  overflow?: OverflowMode
  // 文字列セルの最大幅（既定 DEFAULT_OVERFLOW_MAX_WIDTH_CLASS）
  maxWidthClass?: string
  // サムネイル画像など、セルの余白を詰めて中身を行の高さいっぱいに出す列
  cellPadding?: 'none'
  // その行を表す名前列（証明書番号・物件名など）1本に付ける。太字で出し、一覧の一番左のデータ列に置く
  keyColumn?: boolean
  // 行の操作列（RowActions を入れる右端の列）。直接指定せず rowActionsColumn() で作る。
  // 見出しを出さずに列幅を中身に合わせ、見出しとボタンを右端で揃える
  rowActions?: boolean
}

// 表示列の設定などで列定義を共通の map から作る画面が、操作列だけ同じ見た目にするための部分定義
export const rowActionsColumnLook = { title: <span className="sr-only">操作</span>, rowActions: true } as const

// 行の操作列の定義。見出しの文字は読み上げ用にだけ持ち、画面には出さない。
// 見出し「操作」を左寄せで出すと右寄せのボタンと位置がずれるうえ、ボタンの文字で操作だと分かるため
// （docs/develop/client_ui/components/data-table.md「行の操作」）
export function rowActionsColumn<Row>(render: (row: Row) => ReactNode): DataTableColumn<Row> {
  return { key: 'actions', ...rowActionsColumnLook, render }
}

export type DataTableRowSelection<Row> = {
  disabled?: boolean
  isSelected: (row: Row) => boolean
  allPageSelected: boolean
  somePageSelected: boolean
  onToggleSelectAllPage: () => void
  onToggleRow: (row: Row) => void
  rowLabel: (row: Row) => string
}

export type DataTableExpandable<Row> = {
  isExpanded: (row: Row) => boolean
  onToggle: (row: Row) => void
  expandedRowTitle: (row: Row) => ReactNode
  expandedRowRender: (row: Row) => ReactNode
  rowExpandable?: (row: Row) => boolean
  detailsId?: (row: Row) => string
  // 展開トグルの aria-label に使う業務キー（証明書番号・参照番号等）。rowKey は React key を
  // 兼ねるため、no のように業務上ユニークでない値を渡すと行の重複描画・選択破綻を招く
  // （2026/08 レビューで発覚）。rowLabel を分離し、aria-label 生成は rowLabel を優先する
  rowLabel?: (row: Row) => string
}

export type DataTableProps<Row> = {
  columns: ReadonlyArray<DataTableColumn<Row>>
  rows: readonly Row[]
  rowKey: (row: Row) => string
  rowTestId?: (row: Row) => string
  // 検索の一致・紐付いた明細など、注目させたい行。選択中の行の色が優先
  rowHighlighted?: (row: Row) => boolean
  testIdPrefix: string
  loading?: boolean
  emptyMessage?: string
  sorters?: readonly DataTableSorter[]
  onChangeSorters?: (next: DataTableSorter[]) => void
  rowSelection?: DataTableRowSelection<Row>
  expandable?: DataTableExpandable<Row>
  toolbar?: ReactNode
  selectionActions?: ReactNode
  selectedCount?: number
  banners?: ReactNode
  pagination?: {
    page: number
    pageSize: number
    totalCount: number
    onChangePage: (page: number) => void
    // ツールバー帯の右端にもページャを出す。行数の多い一覧（ミルシート・加工証明・発注）で、
    // 下端までスクロールせずにページを送れるようにするための opt-in
    showAtTop?: boolean
  }
  // API仕様（docs/issues/20260826_04_client-data-table.md）の DataTableRowSelection には
  // 「選択を解除」のハンドラが無く、選択時ツールバー（SelectionActionBar）の再現に不足していたため
  // 最小追加した口。未指定でも壊れないよう、選択解除ボタンは何もしないフォールバックにする
  onClearSelection?: () => void
}

const DEFAULT_EMPTY_MESSAGE = '該当するデータがありません'

// TableColumnMenu の一覧項目ラベルは string 前提だが、DataTableColumn.title は ReactNode（API仕様どおり）。
// 実運用の4テーブルではタイトルはいずれも文字列のため、文字列ならそのまま使い、それ以外（アイコン等）は
// key をフォールバック表示にする（表示列メニューが読めない事態を避けるための最小対応）。
// 引数の型は render 関数を含まない構造的部分型にし、DataTableColumn<Row> の Row（関数引数の反変性）に
// 依存しないようにする
function columnMenuLabel(column: { key: string; title: ReactNode }): string {
  return typeof column.title === 'string' ? column.title : column.key
}

// onClearSelection 未指定時のフォールバック（SelectionActionBar の onClear は必須 prop のため）。
// no-empty-function に触れないよう `=> undefined` にする（このリポジトリの既存 no-op と同じ書き方）
function noopClearSelection() {
  return undefined
}

// 再取得中（loading）は、直前に読み込み終えた値を出し続ける。
// 呼び出し側の多くはページ送り・絞り込みのたびにデータを null に戻して取り直すため、そのままだと
// 再取得中は rows が空・totalCount が 0 になり、スケルトンへの切り替えと上部ページャの消失・再出現で
// 一覧全体がチラつく（2026/10/30 請求一覧で FB）。テーブル側で前回の値を保持すれば、画面ごとの
// 取得ロジックに手を入れずに「前のデータを出したままスピナーを重ねる」表示へ揃えられる。
// 読み込み完了（loading=false）のときは常に最新の値をそのまま出すので、エラー・0件の表示は変わらない
function useRetainedWhileLoading<T>(value: T, loading: boolean): T {
  const [retained, setRetained] = useState(value)
  // レンダー中の setState は「props の変化に合わせて state を調整する」React 公式のパターン。
  // 同じ props のまま即座に再レンダーされるため、配列が毎回作り直される呼び出し側でもループしない
  if (!loading && retained !== value) {
    setRetained(value)
  }
  return loading ? retained : value
}

// 汎用テーブルコンポーネント。ui/data-table/ 配下の既存部品（Shell・ColumnMenu・SortIndicator・
// ExpandToggleButton・EmptyRow・styles）を束ね、発注照合基準（PR #228）の見た目を列定義から再現する。
// マークアップ・機能ロジックはこの中に閉じ込め、画面側は列定義・状態・render prop だけを持つようにする
export function DataTable<Row>({
  columns,
  rows: latestRows,
  rowKey,
  rowTestId,
  rowHighlighted,
  testIdPrefix,
  loading,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  sorters = [],
  onChangeSorters,
  rowSelection,
  expandable,
  toolbar,
  selectionActions,
  selectedCount,
  banners,
  pagination: latestPagination,
  onClearSelection,
}: DataTableProps<Row>) {
  const rows = useRetainedWhileLoading(latestRows, loading === true)
  // ページ番号は押した直後から新しいページを示したいので最新の値を使い、件数（totalCount）だけを保持する。
  // 件数まで最新にすると、再取得中に 0 件扱いになって上部ページャが消える（上の useRetainedWhileLoading 参照）
  const totalCount = useRetainedWhileLoading(latestPagination?.totalCount, loading === true)
  const pagination = isSome(latestPagination)
    ? { ...latestPagination, totalCount: totalCount ?? latestPagination.totalCount }
    : undefined
  // 表示列メニューの非表示状態はテーブル内部 state（永続化しない。既存3画面と同じ方針）
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(new Set())

  const hidableColumns = columns.filter((column) => column.hidable === true)
  const visibleColumns = columns.filter((column) => column.hidable !== true || !hiddenColumnKeys.has(column.key))

  // sticky 列は「選択チェックボックス（あれば）→ 最初の fixed:'left' 列」の1本という前提
  // （docs/develop/client_ui/components/data-table.md の基本形＝キー列1本の左固定。参照実装 orders-table.tsx と同型）。
  // 2本目以降の fixed:'left' 列は sticky 化しない（複数キー列固定は現行4テーブルに実例が無く、
  // 実測幅が要る sticky 積み上げは過剰実装になるため見送り）
  const fixedColumnIndex = visibleColumns.findIndex((column) => column.fixed === 'left')
  const fixedColumnLeftClass = isSome(rowSelection) ? 'left-12' : 'left-0'
  // 右端の操作を横スクロール後も使えるように、最後の右固定指定列だけを固定する。
  const fixedRightColumnIndex = visibleColumns.findLastIndex((column) => column.fixed === 'right')
  // 展開トグルは「キー列（先頭の fixed 列）」のセルに内包する（orders-table と同じ構造）。
  // fixed 列が無いテーブルで expandable を使う場合のみ、先頭列をフォールバックの内包先にする
  const expandAnchorIndex = fixedColumnIndex === -1 ? 0 : fixedColumnIndex

  const toggleColumn = (key: string) => {
    setHiddenColumnKeys((current) => {
      const next = new Set(current)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleSortClick = (key: string) => {
    if (!isSome(onChangeSorters)) {
      return
    }
    onChangeSorters(nextSorters(sorters, key))
  }

  function renderColumnHeader(column: DataTableColumn<Row>) {
    const activeSorter = sorters.find((sorter) => sorter.key === column.key)
    if (column.sortable !== true) {
      return (
        <span className={cn('inline-flex items-center gap-1', column.numeric === true && 'justify-end')}>
          {column.title}
          {column.help}
        </span>
      )
    }
    // column.help（ツールチップトリガー）はソートボタンの外に置く。ボタンの内側に置くと、
    // ヘルプアイコン上のクリックがボタンに吸収され、ヘッダー中央をクリックしてもソートが
    // 発火しない事象があった（2026/08 ブラウザ検証で発覚）
    return (
      <span className={cn('inline-flex items-center gap-1', column.numeric === true && 'justify-end')}>
        <button
          type="button"
          className="inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-sort-${column.key}`}
          onClick={() => {
            handleSortClick(column.key)
          }}
        >
          {column.title}
          <SortIndicator active={isSome(activeSorter)} order={activeSorter?.order} />
        </button>
        {column.help}
      </span>
    )
  }

  // 選択中件数は selectedCount（全件選択時などページ外を含む件数）を優先し、未指定なら
  // 現在表示中の行から数える（rowSelection だけを渡す最小構成でもツールバーが動くようにするため）
  const fallbackSelectedCount = isSome(rowSelection) ? rows.filter((row) => rowSelection.isSelected(row)).length : 0
  const effectiveSelectedCount = selectedCount ?? fallbackSelectedCount
  const showSelectionToolbar = isSome(rowSelection) && effectiveSelectedCount > 0
  // toolbar 帯は「選択時ツールバー・呼び出し側の toolbar・列表示メニュー」のいずれかを持つときだけ描画する
  // （DataTableShell 側で isSome(toolbar) 判定するため、内容が無ければ undefined を渡す）
  const hasToolbarContent = showSelectionToolbar || isSome(toolbar) || hidableColumns.length > 0

  const bodyColSpan = (isSome(rowSelection) ? 1 : 0) + visibleColumns.length

  // 初回ロード（表示すべき行がまだ無い）はスケルトン行に、絞り込み変更等の再取得（既存行がある）は
  // 半透明化 + オーバーレイのスピナーに出し分ける。両方同時に出すと二重のローディング表示になるため、
  // シェルの loading（オーバーレイ表示）は「既存行がある」ときだけ true にする
  const showSkeleton = loading === true && rows.length === 0
  const showOverlay = loading === true && rows.length > 0
  // skeleton の行数は pagination.pageSize（無ければ 8）をそのまま使い、上限は設けない。
  // 目的は「読み込み前後でスクロール高さを変えない」ことなので、pageSize=100 の一覧なら
  // 100行のプレースホルダを出す必要がある。プレースホルダは中身を持たない div の羅列で
  // 描画コストは無視できるため、10行に切って高さを詰めると初回ロード後に一覧が大きく伸びてしまう
  // （2026/08 レビューFBで発覚。以前は明示指定も上限10に切られる紛らわしい仕様だった）
  const resolvedSkeletonRowCount = pagination?.pageSize ?? 8

  const toolbarContent =
    showSelectionToolbar && isSome(rowSelection) ? (
      <SelectionActionBar
        selectedCount={effectiveSelectedCount}
        onClear={onClearSelection ?? noopClearSelection}
        actions={selectionActions}
      />
    ) : (
      <>
        {toolbar}
        {hidableColumns.length > 0 && (
          <TableColumnMenu
            columns={hidableColumns.map((column) => ({ key: column.key, label: columnMenuLabel(column) }))}
            hidden={hiddenColumnKeys}
            onToggle={toggleColumn}
          />
        )}
      </>
    )

  return (
    <DataTableShell
      testId={testIdPrefix}
      // aria-busy・inert は「読み込み中かどうか」全体（skeleton・オーバーレイのどちらも）に効かせる。
      // オーバーレイの見た目（showOverlay）とは分けて渡す（data-table-shell.tsx 側のコメント参照）
      loading={loading === true}
      showOverlay={showOverlay}
      banners={banners}
      // 上部ページャは複数ページあるときだけ出す。1ページに収まる一覧では下部と同じ内容が
      // 2つ並ぶだけで、ツールバー帯の視覚ノイズになる
      topPagination={
        isSome(pagination) && pagination.showAtTop === true && pagination.totalCount > pagination.pageSize ? (
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalCount={pagination.totalCount}
            onChangePage={pagination.onChangePage}
            ariaLabel={`${testIdPrefix}の上部ページネーション`}
            testId={`${testIdPrefix}-pager-top`}
            // 上部は選択件数・一括アクションと同じ帯に並ぶため、ページ番号を詰めて幅を抑える
            compact
          />
        ) : undefined
      }
      // 下部ページャの testId / aria-label は上部の追加前から変えない（既存の story・e2e が参照するため）
      // TablePagination が null を返す条件（0件・1ページ目で収まる）では渡さない。
      // 渡すと Shell 側の border-t 付きラッパーだけが残り、テーブル下端の線が二重に見える
      pagination={
        isSome(pagination) && hasPagesToShow(pagination) ? (
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalCount={pagination.totalCount}
            onChangePage={pagination.onChangePage}
            ariaLabel={`${testIdPrefix}のページネーション`}
            testId={`${testIdPrefix}-pager`}
          />
        ) : undefined
      }
      toolbar={hasToolbarContent ? toolbarContent : undefined}
    >
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className={dataTableHeadRowClass}>
            {isSome(rowSelection) && (
              <th className="sticky left-0 z-10 w-12 bg-muted px-4 py-3 text-left">
                <CheckboxControl
                  disabled={rowSelection.disabled}
                  checked={rowSelection.allPageSelected}
                  indeterminate={rowSelection.somePageSelected}
                  onChange={rowSelection.onToggleSelectAllPage}
                  label="すべて選択"
                />
              </th>
            )}
            {visibleColumns.map((column, index) => {
              const isFixedRight = index === fixedRightColumnIndex
              const isFixed = index === fixedColumnIndex || isFixedRight
              return (
                <th
                  key={column.key}
                  className={
                    // dataTableThClass（px-4 py-3 font-semibold whitespace-nowrap text-muted-foreground）を土台にして、
                    // 固定列に必要な差分（sticky・背景・左揃え）だけ足す。dataTableThClass 側の変更に固定列も自動で追従させるため
                    isFixed
                      ? cn(
                          dataTableThClass,
                          'sticky z-10 bg-muted',
                          column.rowActions === true ? 'w-px text-right' : 'text-left',
                          isFixedRight ? 'right-0' : fixedColumnLeftClass,
                          column.minWidthClass,
                          isFixedRight ? 'shadow-[inset_1px_0_0_var(--color-border)]' : keyColumnShadowClass,
                        )
                      : cn(
                          dataTableThClass,
                          column.minWidthClass,
                          column.numeric === true || column.rowActions === true ? 'text-right' : 'text-left',
                          column.rowActions === true && 'w-px',
                        )
                  }
                >
                  {renderColumnHeader(column)}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {showSkeleton &&
            // プレースホルダ行は行データを持たないため index キーで問題ない
            Array.from({ length: resolvedSkeletonRowCount }).map((_, index) => (
              <tr
                key={`skeleton-row-${index}`}
                className={dataTableRowClass(false)}
                data-testid={`${testIdPrefix}-skeleton-row`}
              >
                {isSome(rowSelection) && (
                  <td className={cn('sticky left-0 z-10 px-4 py-3', stickyCellBgClass(false))}>
                    <div className="size-4 animate-pulse rounded bg-muted" />
                  </td>
                )}
                {visibleColumns.map((column, colIndex) => {
                  const isFixedRight = colIndex === fixedRightColumnIndex
                  const isFixed = colIndex === fixedColumnIndex || isFixedRight
                  return (
                    <td
                      key={column.key}
                      className={
                        isFixed
                          ? cn(
                              dataTableTdClass,
                              'sticky z-10',
                              isFixedRight ? 'right-0' : fixedColumnLeftClass,
                              column.minWidthClass,
                              isFixedRight ? 'shadow-[inset_1px_0_0_var(--color-border)]' : keyColumnShadowClass,
                              stickyCellBgClass(false),
                              column.rowActions === true && 'w-px',
                            )
                          : cn(dataTableTdClass, column.minWidthClass, column.rowActions === true && 'w-px')
                      }
                    >
                      {/* プレースホルダの高さ（h-5 = 20px）は実セルの行高（text-sm の line-height）と
                          一致させる。dataTableTdClass の py-3（上下12px）と合わせて実データ行と同じ
                          44px にするため（h-4=16pxのままだと10行で40pxずれていた。2026/08 レビューFB） */}
                      <div
                        className={cn(
                          'h-5 animate-pulse rounded bg-muted',
                          column.numeric === true ? 'ml-auto w-12' : 'w-24',
                        )}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          {!showSkeleton && rows.length === 0 && (
            <DataTableEmptyRow colSpan={Math.max(bodyColSpan, 1)} message={emptyMessage} />
          )}
          {!showSkeleton &&
            rows.map((row) => {
              const key = rowKey(row)
              const testId = rowTestId?.(row)
              const selected = isSome(rowSelection) && rowSelection.isSelected(row)
              const expanded = isSome(expandable) && expandable.isExpanded(row)
              const highlighted = rowHighlighted?.(row) ?? false
              const rowBg = stickyCellBgClass(selected, highlighted)
              const canExpand = isSome(expandable) && (expandable.rowExpandable?.(row) ?? true)
              const detailsId = expandable?.detailsId?.(row) ?? `${testIdPrefix}-details-${key}`
              // rowLabel 未指定の画面向けフォールバック（rowKey の値をそのまま使う。従来の挙動）
              const expandLabel = expandable?.rowLabel?.(row) ?? key

              return (
                <Fragment key={key}>
                  <tr className={dataTableRowClass(selected, highlighted)} data-testid={testId}>
                    {isSome(rowSelection) && (
                      <td className={cn('sticky left-0 z-10 px-4 py-3 transition-colors', rowBg)}>
                        <CheckboxControl
                          disabled={rowSelection.disabled}
                          checked={selected}
                          onChange={() => {
                            rowSelection.onToggleRow(row)
                          }}
                          label={rowSelection.rowLabel(row)}
                        />
                      </td>
                    )}
                    {visibleColumns.map((column, index) => {
                      const isFixedRight = index === fixedRightColumnIndex
                      const isFixed = index === fixedColumnIndex || isFixedRight
                      const isExpandAnchor = isSome(expandable) && index === expandAnchorIndex
                      const rawRendered = column.render(row)
                      // 文字列だけを自動で省略対象にする。数値・装飾付きのノードは長文になりにくく、
                      // 中のフォーカスリング等を overflow で切らないよう触らない
                      const rendered =
                        typeof rawRendered === 'string' ? (
                          <OverflowText
                            text={rawRendered}
                            mode={column.overflow}
                            maxWidthClass={column.maxWidthClass}
                          />
                        ) : (
                          rawRendered
                        )
                      return (
                        <td
                          key={column.key}
                          className={cn(
                            // dataTableTdClass（px-4 py-3）を土台に、固定列に必要な差分（sticky・境界影・選択色）だけ足す
                            isFixed
                              ? cn(
                                  dataTableTdClass,
                                  'sticky z-10 whitespace-nowrap transition-colors',
                                  isFixedRight ? 'right-0' : fixedColumnLeftClass,
                                  isFixedRight ? 'shadow-[inset_1px_0_0_var(--color-border)]' : keyColumnShadowClass,
                                  rowBg,
                                )
                              : cn(dataTableTdClass, 'whitespace-nowrap'),
                            column.numeric === true ? 'text-right tabular-nums text-foreground' : 'text-foreground',
                            column.rowActions === true && 'w-px text-right',
                            column.keyColumn === true && 'font-semibold',
                            // h-px は「セルの高さを行の高さに合わせて確定させる」ための指定。これが無いと中身の
                            // h-full が解決されず、ボタンのある行（52px）でサムネイルの上下に隙間が残る
                            column.cellPadding === 'none' && 'h-px p-0',
                          )}
                        >
                          {isExpandAnchor ? (
                            <div className="flex items-center gap-2">
                              {canExpand && isSome(expandable) && (
                                <ExpandToggleButton
                                  expanded={expanded}
                                  onClick={() => {
                                    expandable.onToggle(row)
                                  }}
                                  ariaControls={detailsId}
                                  ariaLabel={`${expandLabel}の明細を${expanded ? '閉じる' : '開く'}`}
                                />
                              )}
                              {rendered}
                            </div>
                          ) : (
                            rendered
                          )}
                        </td>
                      )
                    })}
                  </tr>
                  {isSome(expandable) && expanded && (
                    <tr id={detailsId} className="border-b border-border bg-accent/55">
                      <td colSpan={Math.max(bodyColSpan, 1)} className="p-3">
                        <div
                          // 明細枠を表示領域に留め、親を右へ動かしてから展開しても内容を見失わないようにする。
                          className="sticky left-3 w-[calc(100cqw-1.5rem)] rounded-xl border border-primary/15 bg-card p-4 shadow-xs"
                        >
                          <div className="mb-3 flex items-baseline gap-2">
                            <h3 className="font-bold text-foreground">{expandable.expandedRowTitle(row)}</h3>
                          </div>
                          {expandable.expandedRowRender(row)}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
        </tbody>
      </table>
    </DataTableShell>
  )
}
